import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'

// design/js/charts.js の `readableOn()` と同じく、 ecloud に対応トークンが無い
// raw hex を使う (該当コメント: 同ファイル L40 "[DS差異] 任意の系列色に対する
// 動的コントラストのため #1a1a1a を使用")。
// eslint-disable-next-line no-restricted-syntax -- design/js/charts.js readableOn と同じ raw hex (DS にダークテキストトークンが無い)
const DARK_TEXT_COLOR = '#1a1a1a'
const LIGHT_TEXT_COLOR = LightModeSemanticColors.Text.highEmphasisInverse.value

const HEX_COLOR_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

type Rgb = { r: number; g: number; b: number }

function parseHexColor(input: string): Rgb | null {
  const match = HEX_COLOR_PATTERN.exec(input.trim())
  if (match == null) return null

  const digits = match[1]
  const hex =
    digits.length === 3
      ? digits
          .split('')
          .map((c) => c + c)
          .join('')
      : digits

  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  }
}

/** sRGB の 1 チャンネル (0-255) を WCAG 相対輝度計算用の線形値に変換する。 */
function srgbChannelToLinear(channel255: number): number {
  const c = channel255 / 255
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

/** WCAG 2.x 相対輝度。 https://www.w3.org/TR/WCAG21/#dfn-relative-luminance */
function relativeLuminance({ r, g, b }: Rgb): number {
  return (
    0.2126 * srgbChannelToLinear(r) +
    0.7152 * srgbChannelToLinear(g) +
    0.0722 * srgbChannelToLinear(b)
  )
}

/** WCAG 2.x コントラスト比。 https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio */
function contrastRatio(luminanceA: number, luminanceB: number): number {
  const lighter = Math.max(luminanceA, luminanceB)
  const darker = Math.min(luminanceA, luminanceB)
  return (lighter + 0.05) / (darker + 0.05)
}

const DARK_TEXT_LUMINANCE = relativeLuminance(
  parseHexColor(DARK_TEXT_COLOR) as Rgb,
)
const LIGHT_TEXT_LUMINANCE = 1

/** WCAG 2.x AA の通常文字 (< 18.66px bold) が要求するコントラスト比。 */
const AA_TEXT_CONTRAST = 4.5

function toHex({ r, g, b }: Rgb): string {
  const pair = (v: number): string =>
    Math.round(v).toString(16).padStart(2, '0')
  return `#${pair(r)}${pair(g)}${pair(b)}`
}

/** 各チャンネルを同率で縮める = **色相・彩度比を保ったまま**暗くする。 */
function scaleRgb({ r, g, b }: Rgb, k: number): Rgb {
  return { r: Math.round(r * k), g: Math.round(g * k), b: Math.round(b * k) }
}

/**
 * 白文字が `AA_TEXT_CONTRAST` に届く**いちばん明るい**縮小率を二分探索する。
 * 縮小率に対してコントラスト比は単調増加なので、 24 回で 1/255 未満まで詰まる。
 */
function darkenForLightText(rgb: Rgb): Rgb {
  let tooDark = 0
  let tooLight = 1
  for (let i = 0; i < 24; i++) {
    const mid = (tooDark + tooLight) / 2
    const ratio = contrastRatio(
      relativeLuminance(scaleRgb(rgb, mid)),
      LIGHT_TEXT_LUMINANCE,
    )
    if (ratio >= AA_TEXT_CONTRAST) tooDark = mid
    else tooLight = mid
  }
  // 二分探索は実数で詰めるので、 整数へ丸めた結果が 4.5 をわずかに割ることがある。
  // 割っている間だけ 1/255 ずつ下げる (実測 0〜1 段で収まる)。
  let scale = tooDark
  for (let i = 0; i < 8; i++) {
    const candidate = scaleRgb(rgb, scale)
    const ratio = contrastRatio(
      relativeLuminance(candidate),
      LIGHT_TEXT_LUMINANCE,
    )
    if (ratio >= AA_TEXT_CONTRAST) return candidate
    scale = Math.max(scale - 1 / 255, 0)
  }
  return scaleRgb(rgb, scale)
}

/** design `readableOn` と同じ YIQ 閾値 150 判定。 */
function designTextColor({ r, g, b }: Rgb): string {
  return (r * 299 + g * 587 + b * 114) / 1000 >= 150
    ? DARK_TEXT_COLOR
    : LIGHT_TEXT_COLOR
}

export type BadgeColors = {
  /** バッジの地色。 系列色そのまま、 または系列色を沈めた同色相の暗色。 */
  background: string
  /** バッジのラベル文字色。 */
  color: string
}

/**
 * 系列色バッジ (ツールチップの「計画値」「低圧」等) の**地色と文字色の組**。
 *
 * design/js/charts.js は系列色をそのまま地に敷き、 文字色を `readableOn()`
 * (YIQ 閾値 150) で白/黒に振る。 本関数もまず**文字色は design と同じ判定**で決め、
 * その組み合わせが AA (通常文字 4.5:1) に届かない時だけ**地色を同色相のまま沈める**。
 *
 * why not `readableTextColor` (WCAG コントラスト最大の色を選ぶ): 数値上は最大でも、
 * 青・青緑・くすんだピンクの系列では黒文字が沈んで**かえって読みにくい**
 * (WCAG 2.x の相対輝度は青チャンネルの寄与が 0.0722 しかなく、 中明度の青を
 * 実際より暗く見積もる)。 実装は brand-cyan (`#597eff`) / brand-teal (`#00b0b2`) /
 * 低圧 (`#b76593`) の 3 色で design と逆の黒文字を選んでおり、 デザイナー実機確認で
 * 「計画値・低圧・降水量・露点バッジの視認性が悪い」 と指摘された (#7633、light mode)。
 *
 * why not design の値をそのまま使う: 白文字のままだと brand-cyan 3.59:1 /
 * 低圧 3.97:1 / brand-teal 2.67:1 で AA を割る (`readableTextColor` が
 * #5562 のレビュー指摘で導入された理由そのもの)。 地色を最小限沈めれば
 * **design の見え方 (白文字) と AA の両方**が立つ。 沈める量は 4.5:1 ちょうどまでで、
 * 実測 `#597eff`→`#4e6ee0` / `#b76593`→`#aa5e88` / `#00b0b2`→`#008486`。
 * すでに足りている系列色 (実績値・気温・特別高圧 等) は**一切変えない**。
 */
export function badgeColors(seriesColor: string): BadgeColors {
  const rgb = parseHexColor(seriesColor)
  // hex として解釈できない入力は design の fallback (`text-inverse` = 白) に倣う。
  if (rgb == null) return { background: seriesColor, color: LIGHT_TEXT_COLOR }

  const color = designTextColor(rgb)
  const textLuminance =
    color === DARK_TEXT_COLOR ? DARK_TEXT_LUMINANCE : LIGHT_TEXT_LUMINANCE
  if (
    contrastRatio(relativeLuminance(rgb), textLuminance) >= AA_TEXT_CONTRAST
  ) {
    return { background: seriesColor, color }
  }

  // design が黒文字を選ぶのは明色 (YIQ >= 150) の時だけで、 そこが 4.5:1 を割ることは
  // 現行の系列色には無い。 仮に将来割った場合は「地を沈めて白文字」へ倒す
  // (明色を明るくしても黒文字のコントラストは頭打ちで、 系列色から離れる一方のため)。
  return {
    background: toHex(darkenForLightText(rgb)),
    color: LIGHT_TEXT_COLOR,
  }
}
